import * as tslib_1 from "tslib";
import { ClientErrorCodes, ErrorTranslationKeys, } from '@models';
import { Logger } from '@services/logger/abstract.logger';
import { TranslatableError } from '@utils/translatable-error';
import cloneDeep from 'lodash-es/cloneDeep';
import { UserStateManager } from 'main/app-state/generics/user-state-manager';
import { BehaviorSubject, iif, Observable, throwError, } from 'rxjs';
import { map, switchMap, tap, } from 'rxjs/operators';
import { HostLicenseService } from './host-license.service';
import { TenantLicenseService } from './tenant-license.service';
var emptyLicense = {
    id: 0,
    isRegistered: false,
    isExpired: true,
    features: {
        ANL: false,
    },
    attendedConcurrent: false,
    developmentConcurrent: false,
    studioXConcurrent: false,
    allowed: {
        unattended: 0,
        attended: 0,
        nonProduction: 0,
        development: 0,
        studioX: 0,
    },
    used: {
        unattended: 0,
        attended: 0,
        nonProduction: 0,
        development: 0,
        studioX: 0,
    },
};
var LicenseManager = /** @class */ (function (_super) {
    tslib_1.__extends(LicenseManager, _super);
    function LicenseManager(injector, _user) {
        var _this = _super.call(this, _user) || this;
        _this._user = _user;
        _this._state = new BehaviorSubject(null);
        _this._hostLicenseService = injector.get(HostLicenseService);
        _this._tenantLicenseService = injector.get(TenantLicenseService);
        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        _this._logStateChanges('License', injector.get(Logger));
        return _this;
    }
    LicenseManager.prototype.fetch = function () {
        var _this = this;
        if (this._user.authState === 0 /* NotLoggedIn */) {
            return this.clean();
        }
        var licenseSource = this._user.loggedInAsHost ?
            this._hostLicenseService.get({ top: 1 })
                .pipe(map(function (license) {
                return !!license.data && !!license.data[0] ?
                    license.data[0] :
                    cloneDeep(emptyLicense);
            })) :
            this._tenantLicenseService.get(undefined, '/odata/Settings/UiPath.Server.Configuration.OData.GetLicense');
        return licenseSource
            .pipe(tap(function (license) { return _this._state.next(license); }));
    };
    LicenseManager.prototype.clean = function () {
        var _this = this;
        return new Observable(function (subscriber) {
            _this._state.next(null);
            subscriber.next(null);
            subscriber.complete();
        });
    };
    LicenseManager.prototype.uploadLicense = function (file) {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.uploadLicense(file), this._tenantLicenseService.uploadLicense(file))
            .pipe(switchMap(function () { return _this.fetch(); })));
    };
    LicenseManager.prototype.deleteLicense = function () {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.delete(this.value.id), this._tenantLicenseService.deleteLicense())
            .pipe(switchMap(function () { return _this.fetch(); })));
    };
    LicenseManager.prototype.activateLicenseOnline = function (licenseKey, environment, email) {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.activateLicenseOnline(licenseKey, environment, email), this._tenantLicenseService.activateLicenseOnline(licenseKey, environment, email))
            .pipe(switchMap(function () { return _this.fetch(); })));
    };
    LicenseManager.prototype.activateLicenseOffline = function (file) {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.activateLicenseOffline(file), this._tenantLicenseService.activateLicenseOffline(file))
            .pipe(switchMap(function () { return _this.fetch(); })));
    };
    LicenseManager.prototype.getLicenseOffline = function (licenseKey, environment, email) {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.getLicenseOffline(licenseKey, environment, email), this._tenantLicenseService.getLicenseOffline(licenseKey, environment, email)));
    };
    LicenseManager.prototype.updateLicenseOnline = function () {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.updateLicenseOnline(), this._tenantLicenseService.updateLicenseOnline())
            .pipe(switchMap(function () { return _this.fetch(); })));
    };
    LicenseManager.prototype.deactivateLicenseOnline = function (tenantId) {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.deactivateLicenseOnline(tenantId), this._tenantLicenseService.deactivateLicenseOnline()));
    };
    LicenseManager.prototype.getDeactivateLicenseOffline = function (tenantId) {
        var _this = this;
        return iif(function () { return _this._user.authState === 0 /* NotLoggedIn */; }, throwError(new TranslatableError(ErrorTranslationKeys.SessionExpired, ClientErrorCodes.SessionExpired)), iif(function () { return _this._user.loggedInAsHost; }, this._hostLicenseService.getDeactivateLicenseOffline(tenantId), this._tenantLicenseService.getDeactivateLicenseOffline()));
    };
    return LicenseManager;
}(UserStateManager));
export { LicenseManager };
